import { skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdSitesFacetedSearchDropoff,
  getV1CompaniesCompanyIdSitesFacetedSearchPickup,
  GetV1CompaniesCompanyIdSitesFacetedSearchPickupData,
  JobState,
  OrderState,
  SiteType,
} from '@treadinc/horizon-api-spec';
import { useCallback, useMemo, useState } from 'react';

import { SiteTypeahead } from '~hooks/useSites/models';
import { extractPagination } from '~services/pagination';

import {
  INFINITE_QUERY_PAGINATION_LIMIT,
  InfiniteQuery,
  InfiniteQueryFetchReason,
  InfiniteQueryParamsState,
} from './constants';

type PickupSitesInfiniteQueryFilters = {
  customerAccountIds?: string[];
  departmentIds?: string[];
  dispatchNumberIds?: string[];
  driverIds?: string[];
  dropoffSiteIds?: string[];
  endDate?: string;
  jobStates?: JobState[];
  orderStates?: OrderState[];
  pickupSiteIds?: string[];
  projectIds?: string[];
  projectExternalIds?: string[];
  requesterIds?: string[];
  siteTypes?: SiteType[];
  startDate?: string;
  vendorAccountIds?: string[];
};

export default function useWaypointInfiniteQuery(
  queryKey: string,
  companyId: string,
  waypointType: 'pickup' | 'drop_off',
  filters: Partial<PickupSitesInfiniteQueryFilters> = {},
): InfiniteQuery {
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<InfiniteQueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? async ({ pageParam, signal }) => {
          const sharedQuery: Omit<
            NonNullable<GetV1CompaniesCompanyIdSitesFacetedSearchPickupData['query']>,
            'filter[job][dropoff_site_ids]'
          > = {
            'page[limit]': INFINITE_QUERY_PAGINATION_LIMIT,
            'page[after]': pageParam?.after,
            'facet[search][query]': queryParams.filters.query.trim() || undefined,
            'filter[job][customer_account_ids]': filters.customerAccountIds?.length
              ? filters.customerAccountIds
              : undefined,
            'filter[dispatch_number_ids]': filters.dispatchNumberIds?.length
              ? filters.dispatchNumberIds
              : undefined,
            'filter[job][driver_ids]': filters.driverIds?.length
              ? filters.driverIds
              : undefined,
            'filter[job][external_ids]': filters.projectExternalIds?.length
              ? filters.projectExternalIds
              : undefined,
            'filter[job][project_ids]': filters.projectIds?.length
              ? filters.projectIds
              : undefined,
            'filter[job][vendor_account_ids]': filters.vendorAccountIds?.length
              ? filters.vendorAccountIds
              : undefined,
            'filter[job][start_date]': filters.startDate || undefined,
            'filter[job][end_date]': filters.endDate || undefined,
            'filter[job][states]': filters.jobStates?.length
              ? filters.jobStates
              : undefined,
            'filter[job][site_types]': filters.siteTypes?.length
              ? filters.siteTypes
              : undefined,
          };

          const response =
            waypointType === 'pickup'
              ? await getV1CompaniesCompanyIdSitesFacetedSearchPickup({
                  path: { 'company-id': companyId },
                  query: {
                    ...sharedQuery,
                    'filter[job][dropoff_site_ids]': filters.dropoffSiteIds?.length
                      ? filters.dropoffSiteIds
                      : undefined,
                  },
                  signal,
                })
              : await getV1CompaniesCompanyIdSitesFacetedSearchDropoff({
                  path: { 'company-id': companyId },
                  query: {
                    ...sharedQuery,
                    'filter[job][pickup_site_ids]': filters.pickupSiteIds?.length
                      ? filters.pickupSiteIds
                      : undefined,
                  },
                  signal,
                });

          const pickupSites = response.data.data.map((pickupSite) => {
            const parsed = SiteTypeahead.parse(pickupSite);

            return { label: parsed.name, value: parsed.id };
          });
          const pagination = extractPagination(response);

          setQueryParams((state) => ({
            ...state,
            pagination: { ...pagination, hasMore: Boolean(pagination.after) },
          }));

          return pickupSites;
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const fetchReason = useMemo(() => {
    if (isFetchingNextPage) {
      return InfiniteQueryFetchReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return InfiniteQueryFetchReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, [queryKey]);

  return { clear, data: allData, fetch, fetchMore, fetchReason, isDummyQuery: false };
}
